import { logger } from '@kuow/core';

const DTD_SCRIPT_URL = 'https://doublethedonation.com/api/js/ddplugin.js';
const DTD_CSS_URL = 'https://doublethedonation.com/api/css/ddplugin.css';
const DTD_CONTAINER_ID = 'dd-company-name-input';

declare global {
  interface Window {
    doublethedonation?: {
      plugin: {
        // load_streamlined_input accepts an optional element parameter
        load_streamlined_input: (element?: HTMLElement | null) => void;
        set_company: (companyId: string | null) => void;
      };
    };
  }
}

// Module-level root reference for Shadow DOM support.
// Initialized lazily to avoid crashing in SSR (Node.js has no `document`).
let dtdRoot: Document | ShadowRoot | null = null;

function getDtdRoot(): Document | ShadowRoot {
  dtdRoot ??= document;
  return dtdRoot;
}

/**
 * Sets the root element for DTD queries (document or shadow root)
 * Call this before loadDoubleTheDonation when rendering inside Shadow DOM
 */
export function setDtdRoot(root: Document | ShadowRoot): void {
  dtdRoot = root;
}

/**
 * Force loads the Double the Donation script, removing any existing script first
 */
function forceLoadScript(url: string, callback?: () => void): void {
  const head = document.getElementsByTagName('head')[0];
  const scripts = Array.from(document.scripts);
  
  // Find and remove any existing script with this URL
  const oldScript = scripts.find(script => script.src === url);
  if (oldScript?.parentNode) {
    oldScript.parentNode.removeChild(oldScript);
  }

  // Create new script element
  const script = document.createElement('script');
  script.type = 'text/javascript';
  script.src = url;
  
  if (callback) {
    script.onload = callback;
  }

  head.appendChild(script);
}

/**
 * Injects DTD CSS into the shadow root
 */
async function injectDtdCssIntoShadow(shadowRoot: ShadowRoot): Promise<void> {
  // Check if CSS is already injected
  if (shadowRoot.querySelector('style[data-dtd-css]')) {
    return;
  }

  try {
    const response = await fetch(DTD_CSS_URL);
    const cssText = await response.text();
    
    const style = document.createElement('style');
    style.setAttribute('data-dtd-css', 'true');
    style.textContent = cssText;
    shadowRoot.appendChild(style);
    
    logger.info('DTD CSS injected into shadow DOM');
  } catch (error) {
    logger.error('Failed to inject DTD CSS into shadow DOM', { error });
  }
}

/**
 * Loads the Double the Donation plugin and initializes it
 * 
 * @param root - Optional root element (document or shadow root) to find the container in.
 *               If not provided, uses the root set via setDtdRoot() or defaults to document.
 */
export function loadDoubleTheDonation(root?: Document | ShadowRoot): Promise<void> {
  const effectiveRoot = root ?? getDtdRoot();
  
  return new Promise((resolve, reject) => {
    const executeCode = async () => {
      try {
        if (!window.doublethedonation?.plugin) {
          throw new Error('DTD plugin not available');
        }

        // Find the container in the specified root
        const container = effectiveRoot.getElementById(DTD_CONTAINER_ID);
        if (!container) {
          throw new Error(`DTD container #${DTD_CONTAINER_ID} not found`);
        }

        // If we're in a shadow root, inject the CSS first
        if (effectiveRoot instanceof ShadowRoot) {
          await injectDtdCssIntoShadow(effectiveRoot);
          logger.info('Double the Donation initializing in Shadow DOM mode');
        }

        // Pass the container element directly to avoid document.getElementById lookup
        window.doublethedonation.plugin.load_streamlined_input(container);
        
        logger.info('Double the Donation plugin loaded successfully');
        resolve();
      } catch (error) {
        logger.error('Failed to initialize Double the Donation plugin', { error });
        reject(error instanceof Error ? error : new Error('Failed to initialize DTD plugin'));
      }
    };

    forceLoadScript(DTD_SCRIPT_URL, () => void executeCode());
  });
}

/**
 * Gets the main DTD container element from the configured root
 */
export function getDtdContainer(): HTMLElement | null {
  const parentElement = getDtdRoot().getElementById(DTD_CONTAINER_ID);
  if (!parentElement) return null;

  const childNodes = parentElement.getElementsByClassName('dtd-streamlined-plugin');
  return childNodes.length > 0 ? (childNodes[0] as HTMLElement) : null;
}

/**
 * Gets a field value from the DTD plugin
 */
export function getDtdFieldValue(fieldName: string): string | null {
  const container = getDtdContainer();
  if (!container) return null;

  const field = container.querySelector<HTMLInputElement>(`[name='${fieldName}']`);
  return field?.value ?? null;
}

/**
 * Sets the company in the DTD plugin
 */
export function setDtdCompany(companyId: string | null): void {
  if (window.doublethedonation?.plugin) {
    window.doublethedonation.plugin.set_company(companyId);
  }
}

export { DTD_CONTAINER_ID };

